import APIService from './api.js';

class AuthService {
  // ===== AUTH METHODS =====
  
  async register(userData) {
    try {
      const response = await APIService.authRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async login(email, password, rememberMe = false) {
    try {
      console.log('🔐 AuthService: Making login request...');
      console.log('🔐 AuthService: Email:', email);
      console.log('🔐 AuthService: RememberMe:', rememberMe);
      console.log('🔐 AuthService: APIService.tenantCode:', APIService.tenantCode);
      
      const requestBody = { 
        email, 
        password, 
        rememberMe, 
        tenantCode: APIService.tenantCode 
      };
      console.log('🔐 AuthService: Request body:', requestBody);
      
      const response = await APIService.authRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
      
      console.log('🎉 AuthService: Response received:', response);
      return response;
    } catch (error) {
      console.error('❌ AuthService: Full error object:', error);
      console.error('❌ AuthService: Error message:', error.message);
      console.error('❌ AuthService: Error stack:', error.stack);
      throw error;
    }
  }

  async googleLogin(googleToken) {
    try {
      const response = await APIService.authRequest('/auth/google', {
        method: 'POST',
        body: JSON.stringify({ 
          googleToken, 
          tenantCode: APIService.tenantCode 
        })
      });
      return response;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  async verifyEmail(userId, token) {
    try {
      const response = await APIService.authRequest('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ userId, token })
      });
      return response;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  }

  async forgotPassword(email) {
    try {
      const response = await APIService.authRequest('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ 
          email, 
          tenantCode: APIService.tenantCode 
        })
      });
      return response;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  async resetPassword(token, newPassword) {
    try {
      const response = await APIService.authRequest('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword })
      });
      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  async refreshToken(refreshToken) {
    try {
      const response = await APIService.authRequest('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken })
      });
      return response;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  async getCurrentUser(token) {
    try {
      const response = await APIService.authRequest('/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  async logout(token) {
    try {
      const response = await APIService.authRequest('/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      return response;
    } catch (error) {
      console.error('Logout error:', error);
      // Don't throw error for logout - we want to clear local data anyway
      return null;
    }
  }

  // ===== PROFILE METHODS =====

  async getProfile(token) {
    try {
      const response = await APIService.authRequest('/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  async updateProfile(token, profileData) {
    try {
      // ✅ Use authRequest instead of request + proper headers
      const response = await APIService.authRequest('/profile', {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',  // ✅ Explicit JSON header
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(profileData)
      });
      return response;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  async getBookingHistory(token, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await APIService.authRequest(`/profile/bookings${queryString ? '?' + queryString : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response;
    } catch (error) {
      console.error('Get booking history error:', error);
      throw error;
    }
  }

  async getSavedLocations(token) {
    try {
      const response = await APIService.authRequest('/profile/locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response;
    } catch (error) {
      console.error('Get saved locations error:', error);
      throw error;
    }
  }

  async createSavedLocation(token, locationData) {
    try {
      const response = await APIService.authRequest('/profile/locations', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(locationData)
      });
      return response;
    } catch (error) {
      console.error('Create saved location error:', error);
      throw error;
    }
  }

  async updateSavedLocation(token, locationId, locationData) {
    try {
      const response = await APIService.authRequest(`/profile/locations/${locationId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(locationData)
      });
      return response;
    } catch (error) {
      console.error('Update saved location error:', error);
      throw error;
    }
  }

  async deleteSavedLocation(token, locationId) {
    try {
      const response = await APIService.authRequest(`/profile/locations/${locationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      return response;
    } catch (error) {
      console.error('Delete saved location error:', error);
      throw error;
    }
  }
}

export default new AuthService();
